import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, Loader2, Check, ExternalLink, KeyRound, Sparkles, Linkedin, Facebook, RefreshCw } from "lucide-react";

export const ZERNIO_AFFILIATE_URL = "https://zernio.link/seb-beckett";
const KEY_RX = /^[A-Za-z0-9_-]{16,200}$/;

// Opens the affiliate link in a real tab (so Zernio's attribution cookie is set by the user's own visit).
export const openZernioAffiliate = (url) => window.open(url || ZERNIO_AFFILIATE_URL, "_blank", "noopener");

export const ZernioUpgradeModal = ({ open, onOpenChange, url }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-card border-border" data-testid="zernio-upgrade-modal">
      <DialogHeader>
        <DialogTitle className="font-display">You've reached your 2 free channels!</DialogTitle>
        <DialogDescription>Connect more Facebook &amp; LinkedIn pages starting at $6/mo.</DialogDescription>
      </DialogHeader>
      <Button onClick={() => { openZernioAffiliate(url); onOpenChange(false); }} data-testid="zernio-upgrade-btn"
        className="w-full bg-primary hover:bg-primary/90 text-white">
        Upgrade Connected Accounts <ExternalLink className="h-4 w-4 ml-2" />
      </Button>
    </DialogContent>
  </Dialog>
);

function SetupModal({ open, onOpenChange, workspaceId, onSaved }) {
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const k = key.trim();
    if (!KEY_RX.test(k)) { toast.error("That doesn't look like a Zernio API key — copy it from Settings > API Keys."); return; }
    setSaving(true);
    try {
      const r = await api.post("/v1/zernio/keys", { workspace_id: workspaceId, zernio_api_key: k });
      toast.success(`Zernio connected · ${r.data.connected_accounts_count} account${r.data.connected_accounts_count === 1 ? "" : "s"} found`);
      setKey("");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not validate that key"));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="zernio-setup-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Connect Zernio in 2 steps</DialogTitle>
          <DialogDescription>Zernio securely holds your Facebook &amp; LinkedIn connections. Your first 2 accounts are free.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-4 text-sm">
          <li className="flex gap-3" data-testid="zernio-step-1">
            <span className="h-6 w-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">1</span>
            <div>
              <p>Create your free Zernio account or sign in via the opened tab to claim your 2 free slots.</p>
              <button onClick={() => openZernioAffiliate()} className="text-xs text-primary underline mt-1" data-testid="zernio-reopen-link">Tab didn't open? Open Zernio</button>
            </div>
          </li>
          <li className="flex gap-3" data-testid="zernio-step-2">
            <span className="h-6 w-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">2</span>
            <div className="flex-1 space-y-2">
              <p>Copy your Zernio API Key from Settings &gt; API Keys and paste it below.</p>
              <Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk_..."
                data-testid="zernio-v1-key-input" className="font-mono text-xs bg-background" onKeyDown={(e) => e.key === "Enter" && save()} />
              <Button onClick={save} disabled={saving || !key.trim()} data-testid="zernio-v1-save-btn" className="w-full bg-primary hover:bg-primary/90 text-white">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />} Save &amp; Validate
              </Button>
              <p className="text-[11px] text-muted-foreground">Encrypted at rest. We never show it again.</p>
            </div>
          </li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

// "Connected Accounts & API Settings" — affiliate onboarding + live free-slot counter.
export const ZernioConnectPanel = ({ workspaceId, onChange }) => {
  const [status, setStatus] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try { setStatus((await api.get(`/v1/zernio/status?workspace_id=${workspaceId}`)).data); }
    catch (e) { setStatus(null); }
    finally { setLoading(false); }
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const start = () => { openZernioAffiliate(status?.affiliate_url); setSetupOpen(true); };
  const count = status?.connected_accounts_count || 0;
  const limit = status?.free_limit || 2;
  const connected = !!status?.connected;

  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-3" data-testid="zernio-connect-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Connected Accounts &amp; API Settings</p>
        {connected && (
          <span className={`text-xs font-mono px-2 py-1 rounded-md border ${count > limit ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"}`} data-testid="zernio-account-counter">
            {count > limit ? `${count} accounts · paid tier` : `${count} / ${limit} Free Accounts Used`}
          </span>
        )}
      </div>
      {!connected ? (
        <>
          <p className="text-xs text-muted-foreground">Every AutoGridSocial user gets 2 free connected Facebook or LinkedIn accounts through our publishing partner Zernio.</p>
          <Button onClick={start} data-testid="zernio-claim-btn" className="bg-primary hover:bg-primary/90 text-white">
            <Sparkles className="h-4 w-4 mr-2" /> Connect Zernio &amp; Claim 2 Free Accounts
          </Button>
        </>
      ) : (
        <>
          {status?.state === "KEY_ERROR" && <p className="text-xs text-rose-400" data-testid="zernio-key-error">Zernio didn't accept the saved key. Re-enter it to resume publishing.</p>}
          <div className="flex flex-wrap gap-2" data-testid="zernio-active-accounts">
            {(status?.active_accounts || []).map((a, i) => (
              <span key={i} className="text-xs flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1">
                {a.platform === "linkedin" ? <Linkedin className="h-3 w-3" /> : <Facebook className="h-3 w-3" />} {a.name || a.platform}
              </span>
            ))}
            {!status?.active_accounts?.length && <span className="text-xs text-muted-foreground">No Facebook or LinkedIn accounts on Zernio yet — connect one below.</span>}
          </div>
          {count >= limit && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="zernio-limit-banner">
              <p className="text-xs text-amber-200">You've reached your 2 free channels! Connect more Facebook &amp; LinkedIn pages starting at $6/mo.</p>
              <Button size="sm" onClick={() => openZernioAffiliate(status?.affiliate_url)} data-testid="zernio-limit-upgrade-btn" className="bg-amber-500 hover:bg-amber-500/90 text-white">Upgrade Connected Accounts</Button>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="zernio-status-refresh" className="border-border">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Refresh
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSetupOpen(true)} data-testid="zernio-replace-key" className="text-muted-foreground">
              <Check className="h-3.5 w-3.5 mr-1.5" /> Replace API key
            </Button>
            <Button size="sm" variant="ghost" data-testid="zernio-key-remove" className="text-rose-400 hover:bg-rose-500/10"
              onClick={async () => { await api.delete(`/workspaces/${workspaceId}/zernio`); toast.success("Zernio account unlinked."); setStatus({ connected: false }); onChange && onChange(); }}>
              Unlink
            </Button>
          </div>
        </>
      )}
      <SetupModal open={setupOpen} onOpenChange={setSetupOpen} workspaceId={workspaceId} onSaved={(d) => { setStatus({ connected: true, ...d }); onChange && onChange(); }} />
      <ZernioUpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} url={status?.affiliate_url} />
    </div>
  );
};
